import { Link } from "react-router-dom";
import { ArrowRight, Check, ChevronRight } from "lucide-react";
import SectionHeading from "@/components/SectionHeading";
import BrochureButton from "@/components/BrochureButton";
import { IMAGES, MODULES, BRAND, MODULE_REPORTS } from "@/lib/constants";

const sectionImages = [IMAGES.quality_inspection, IMAGES.textile, IMAGES.machine, IMAGES.hero_factory];

export default function Product() {
  return (
    <>
      {/* HEADER */}
      <section data-testid="product-hero" className="bg-[#0A1128] py-24 text-white sm:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 items-end gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
                The Product
              </p>
              <h1 className="mt-4 font-serif-display text-4xl font-medium leading-[1.05] sm:text-6xl lg:text-7xl">
                Q-Wis — a single source for product &amp; process quality.
              </h1>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
                Four integrated modules covering quality inspection, process &amp;
                compliance audits, systematic problem solving, and action
                governance — all in one Windows-based solution.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="inline-flex items-center gap-4 rounded-2xl border border-white/15 bg-white/5 p-5 backdrop-blur-md">
                <img src={BRAND.logos.qwis} alt="Q-Wis" className="h-20 w-20 rounded-[22%] object-contain" />
                <div>
                  <p className="font-serif-display text-2xl">Q-Wis</p>
                  <p className="text-sm text-white/60">{BRAND.tagline}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MODULES DEEP DIVE */}
      <section className="bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Modules"
            title="Aligned with global apparel brands. Tested on real factory floors."
            lede="Each module is expertly designed for seamless product and process compliance — and works standalone or as a complete suite."
          />
        </div>

        <div className="mt-16 space-y-24">
          {MODULES.map((m, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div
                key={m.id}
                data-testid={`module-detail-${m.id}`}
                className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8"
              >
                <div
                  className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-12 lg:gap-16 ${
                    reverse ? "lg:[&>div:first-child]:order-2" : ""
                  }`}
                >
                  <div className="overflow-hidden rounded-lg lg:col-span-6">
                    <img
                      src={sectionImages[idx % sectionImages.length]}
                      alt={m.name}
                      className="h-[420px] w-full object-cover transition-transform duration-700 hover:scale-105"
                    />
                  </div>

                  <div className="lg:col-span-6">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
                      Module {String(idx + 1).padStart(2, "0")} · {m.short}
                    </p>
                    <h3 className="mt-4 font-serif-display text-3xl font-medium leading-tight text-[#0A1128] sm:text-4xl lg:text-5xl">
                      {m.name}
                    </h3>
                    <p className="mt-5 text-base leading-relaxed text-[#475569]">
                      {m.description}
                    </p>
                    <ul className="mt-8 space-y-4">
                      {m.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-[#334155]">
                          <Check size={18} className="mt-0.5 shrink-0 text-[#E8772E]" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Per-module reports gallery */}
                <ModuleReports moduleId={m.id} />
              </div>
            );
          })}
        </div>
      </section>

      {/* WHATS INCLUDED */}
      <section data-testid="includes-section" className="bg-[#F8FAFC] py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Implementation"
            title="Enable your quality with Q-Wis."
            lede="The full Q-Wis Windows Application or chosen modules — implemented with hands-on guidance for your operation."
          />
          <div className="mt-12 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {[
              "Base configuration for your operation",
              "Configuration training for occasional variables",
              "Standard data collection reports & recording training",
              "Data entry training",
              "Real-time system support",
              "Annual governance report on the system",
            ].map((t, i) => (
              <div
                key={i}
                className="flex items-start gap-4 rounded-lg border border-[#E2E8F0] bg-white p-6"
              >
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#E8772E]/10 text-[#E8772E]">
                  <Check size={16} />
                </span>
                <p className="text-sm leading-relaxed text-[#334155]">{t}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              data-testid="product-cta-demo"
              to="/contact?intent=demo"
              className="inline-flex items-center gap-2 rounded-md bg-[#E8772E] px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#D66826]"
            >
              Request a Demo <ArrowRight size={16} />
            </Link>
            <BrochureButton variant="outline" label="Download Brochure" />
            <Link
              data-testid="product-cta-pricing"
              to="/pricing"
              className="inline-flex items-center gap-2 rounded-md border border-[#0A1128] bg-transparent px-6 py-3.5 text-sm font-semibold text-[#0A1128] transition-colors hover:bg-[#0A1128] hover:text-white"
            >
              See Pricing
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function ModuleReports({ moduleId }) {
  const info = MODULE_REPORTS[moduleId];
  if (!info) return null;
  const stories = info.stories || [];

  if (stories.length === 0) {
    return (
      <div
        id={`reports-${moduleId}`}
        data-testid={`module-reports-${moduleId}`}
        className="mt-16 scroll-mt-28 rounded-lg border border-dashed border-[#94A3B8] bg-[#F8FAFC] p-10 text-center"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
          Reports & Analytics
        </p>
        <p className="mt-3 text-sm text-[#64748B]">
          Detailed reports for this module are coming soon.
        </p>
      </div>
    );
  }

  return (
    <div
      id={`reports-${moduleId}`}
      data-testid={`module-reports-${moduleId}`}
      className="mt-16 scroll-mt-28 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-8 sm:p-10"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#E8772E]">
        Reports & Analytics
      </p>

      {stories.map((story, storyIdx) => (
        <div
          key={storyIdx}
          data-testid={`module-story-${moduleId}-${storyIdx}`}
          className={storyIdx === 0 ? "mt-3" : "mt-14 border-t border-[#E2E8F0] pt-10"}
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <h4 className="font-serif-display text-2xl leading-tight text-[#0A1128] sm:text-3xl">
                {story.title}
              </h4>
            </div>
            {story.note && (
              <p className="max-w-md text-sm leading-relaxed text-[#475569]">{story.note}</p>
            )}
          </div>

          <div
            className={`mt-8 grid grid-cols-1 gap-8 md:grid-cols-2 ${
              story.steps.length === 3 ? "lg:grid-cols-3" : story.steps.length >= 4 ? "lg:grid-cols-4" : ""
            }`}
          >
            {story.steps.map((s, i) => (
              <div key={i} className="group flex flex-col">
                <div className="mb-3 flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0A1128] text-xs font-semibold text-white">
                    {s.step}
                  </span>
                  <span className="text-sm font-semibold text-[#0A1128]">{s.action}</span>
                  {i < story.steps.length - 1 && (
                    <ChevronRight size={16} className="ml-auto hidden text-[#94A3B8] md:block" />
                  )}
                </div>
                <a
                  href={s.combinedHref || s.srcs?.[0] || s.src}
                  target="_blank"
                  rel="noreferrer"
                  className="block overflow-hidden rounded-md border border-[#E2E8F0] bg-white shadow-sm transition-all group-hover:-translate-y-1 group-hover:shadow-xl"
                >
                  <div className="flex items-center gap-1.5 border-b border-[#E2E8F0] bg-[#F1F5F9] px-3 py-2">
                    <span className="h-2 w-2 rounded-full bg-[#FB7185]" />
                    <span className="h-2 w-2 rounded-full bg-[#FACC15]" />
                    <span className="h-2 w-2 rounded-full bg-[#34D399]" />
                    <span className="ml-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#94A3B8]">
                      Q-Wis · {story.title}
                    </span>
                  </div>
                  {s.srcs ? (
                    <div className="flex flex-col">
                      {s.srcs.map((src, si) => (
                        <img
                          key={si}
                          src={src}
                          alt={`${s.action} — page ${si + 1}`}
                          className={`h-auto w-full object-contain ${si > 0 ? "border-t border-[#E2E8F0]" : ""}`}
                        />
                      ))}
                    </div>
                  ) : (
                    <img
                      src={s.src}
                      alt={s.action}
                      className="h-auto w-full object-contain transition-transform duration-500 group-hover:scale-[1.02]"
                    />
                  )}
                </a>
                <p className="mt-4 text-sm leading-relaxed text-[#475569]">{s.caption}</p>
              </div>
            ))}
          </div>
        </div>
      ))}

      {info.additionalReports?.length > 0 && (
        <div
          data-testid={`module-more-reports-${moduleId}`}
          className="mt-14 border-t border-[#E2E8F0] pt-10"
        >
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <div>
              <h4 className="font-serif-display text-2xl leading-tight text-[#0A1128] sm:text-3xl">
                And there's more.
              </h4>
              <p className="mt-2 text-sm font-semibold uppercase tracking-[0.12em] text-[#475569]">
                Same visualisation power, applied to every angle of your operation.
              </p>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            {info.additionalReports.map((r, i) => (
              <div
                key={i}
                data-testid={`more-report-${moduleId}-${i}`}
                className="flex items-start gap-4 rounded-md border border-[#E2E8F0] bg-white p-5"
              >
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#0A1128]/[0.04] text-[#E8772E]">
                  <ChevronRight size={16} />
                </span>
                <div>
                  <p className="font-serif-display text-lg leading-tight text-[#0A1128]">
                    {r.title}
                  </p>
                  <p className="mt-1 text-sm leading-relaxed text-[#475569]">{r.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
